import type { ReadonlyJSONValue } from "@rocicorp/zero";
import type {
  ChatTool,
  ToolResult,
  ToolUseProps,
} from "../components/Chat/ChatTool";
import {
  CommandRun,
  type CommandSource,
  jsonRecord,
  jsonString,
  rawOutput,
} from "../components/Chat/CommandRun";
import { languageIdForPath } from "../components/codeLanguages";
import { Step } from "../components/Step";

function ExecuteHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const input = jsonRecord(toolUse.input);

  return (
    <Step
      icon="code"
      done={!!result}
      interrupted={interrupted}
      pastParticiple={jsonString(input?.completedDescription) ?? "Done"}
      failure={
        result && failed(result)
          ? (jsonString(input?.failedDescription) ?? "Failed")
          : undefined
      }
      inProgress={
        jsonString(input?.activeDescription)
          ? `${jsonString(input?.activeDescription)}…`
          : "Working…"
      }
    />
  );
}

function failed(result: ToolResult) {
  return result.isError || jsonRecord(result.output)?.status === "error";
}

function sourcesOf(input: Record<string, ReadonlyJSONValue> | null) {
  const language = jsonString(input?.language) ?? "";
  const code = jsonString(input?.code);
  const files = Array.isArray(input?.files) ? input.files : [];
  const sources: CommandSource[] = code ? [{ language, code }] : [];
  for (const file of files) {
    const path = jsonString(jsonRecord(file)?.path);
    const content = jsonString(jsonRecord(file)?.content);
    if (path && content !== undefined) {
      sources.push({
        path,
        language: languageIdForPath(path) ?? "",
        code: content,
      });
    }
  }
  return sources;
}

function outputOf(result: ToolResult) {
  const output = jsonRecord(result.output);
  if (output && "status" in output) {
    return {
      stdout: jsonString(output.output),
      stderr: jsonString(output.log),
      notes: output.status === "pending" ? ["Still running"] : [],
      failure: output.status === "error" ? "Execution failed" : undefined,
    };
  }
  return rawOutput(result);
}

function ExecuteBody({ toolUse, result }: ToolUseProps) {
  return (
    <CommandRun
      sources={sourcesOf(jsonRecord(toolUse.input))}
      {...(result ? outputOf(result) : {})}
    />
  );
}

export const execute: ChatTool = {
  name: "execute",
  Header: ExecuteHeader,
  Body: ExecuteBody,
  PendingBody: ExecuteBody,
};
